/**
 * In-app haptic feedback for incoming messages.
 * - Native (iOS/Android): @capacitor/haptics (requires native rebuild once).
 * - Web/Android browser fallback: navigator.vibrate.
 * Debounced so a single message never triggers a double buzz (the same message
 * can arrive via both the user channel and the conversation channel).
 */
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";

let lastBuzz = 0;
const isNative = () => Capacitor.isNativePlatform();

/** Continuous alarm vibration (reminder alarm) — buzzes every ~1.6s until the
 * returned stop function is called. Native uses Haptics; web uses navigator.vibrate. */
export function startAlarmVibration() {
  let stopped = false;
  const buzz = async () => {
    if (stopped) return;
    try {
      const Cap = window.Capacitor;
      if (Cap && Cap.isNativePlatform && Cap.isNativePlatform()) {
        const { Haptics } = await import("@capacitor/haptics");
        await Haptics.vibrate({ duration: 700 });
        return;
      }
      if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate([500, 200, 500]);
    } catch (e) { /* best-effort */ }
  };
  buzz();
  const iv = setInterval(buzz, 1600);
  return () => {
    stopped = true;
    clearInterval(iv);
    try { navigator.vibrate?.(0); } catch (e) { /* ignore */ }
  };
}

export async function vibrateForMessage() {
  const now = Date.now();
  if (now - lastBuzz < 800) return;
  lastBuzz = now;
  try {
    const Cap = window.Capacitor;
    if (Cap && Cap.isNativePlatform && Cap.isNativePlatform()) {
      const { Haptics } = await import("@capacitor/haptics");
      await Haptics.vibrate({ duration: 250 });
      return;
    }
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(180);
  } catch (e) {
    /* haptics are best-effort */
  }
}

/** Short tactile tick (WhatsApp-style): "start" when the mic actually begins recording,
 *  "lock"/"send" medium, "cancel" a double tick. Native Haptics first, navigator.vibrate on web. */
export async function hapticTick(kind = "start") {
  if (isNative()) {
    try {
      if (kind === "cancel") return await Haptics.notification({ type: NotificationType.Warning });
      if (kind === "send") return await Haptics.notification({ type: NotificationType.Success });
      return await Haptics.impact({ style: kind === "lock" ? ImpactStyle.Heavy : ImpactStyle.Medium });
    } catch (e) { /* fall through to web */ }
  }
  try { navigator.vibrate?.(kind === "cancel" ? [30, 40, 30] : kind === "start" ? 35 : 20); } catch (e) { /* noop */ }
}

/** Light tap for UI selections (menu open, option chosen). */
export async function hapticSelect() {
  if (isNative()) {
    try { return await Haptics.impact({ style: ImpactStyle.Light }); } catch (e) { /* fall through to web */ }
  }
  try { navigator.vibrate?.(15); } catch (e) { /* noop */ }
}
